import { fixUnitlessValue } from '../shared/CSSLength'
import { CONSENT_CHANGED_EVENT, getConsent, isConsentModuleEnabled } from './_cookie-consent'
import { dispatcher } from './dispatcher'
import { saveConsentToCookie } from './module/cookie_consent/cookie'
import { CookieConsent } from './module/cookie_consent/model/CookieConsent'

const CATEGORY_ANALYTICS = 'analytics'
const CATEGORY_MARKETING = 'marketing'

let listenerAdded = false

function decodeIframeMarkup(encodedIframe) {
  if (!encodedIframe) {
    return null
  }

  try {
    return window.atob(encodedIframe)
  } catch (error) {
    return null
  }
}

function parseIframe(markup) {
  if (!markup) {
    return null
  }

  const template = document.createElement('template')
  template.innerHTML = markup.trim()
  const iframe = template.content.querySelector('iframe')

  return iframe || null
}

function getIframeHeight(iframe) {
  if (!iframe) {
    return null
  }

  const iframeHeightAttribute = fixUnitlessValue(iframe.getAttribute('height')?.trim())
  if (iframeHeightAttribute) {
    return iframeHeightAttribute
  }

  const iframeInlineHeight = fixUnitlessValue(iframe.style.height?.trim())
  if (iframeInlineHeight) {
    return iframeInlineHeight
  }

  return null
}

function applyPlaceholderHeight(placeholder) {
  const encodedIframe = placeholder.dataset.cookieConsentIframe
  const iframeMarkup = decodeIframeMarkup(encodedIframe)
  const iframe = parseIframe(iframeMarkup)
  const iframeHeight = getIframeHeight(iframe)

  if (iframeHeight) {
    placeholder.style.height = iframeHeight || ''
  }
}

function hasConsentForCategory(consent, category) {
  if (category === CATEGORY_ANALYTICS) {
    return Boolean(consent && consent.analytics === true)
  }

  if (category === CATEGORY_MARKETING) {
    return Boolean(consent && consent.marketing === true)
  }

  return true
}

function getMessageForCategory(category) {
  if (category === CATEGORY_ANALYTICS) {
    return 'This content requires analytics cookie consent.'
  }

  if (category === CATEGORY_MARKETING) {
    return 'This content requires marketing cookie consent.'
  }

  return 'This content requires cookie consent.'
}

function getButtonLabelForCategory(category) {
  if (category === CATEGORY_ANALYTICS) {
    return 'Allow analytics cookies'
  }

  if (category === CATEGORY_MARKETING) {
    return 'Allow marketing cookies'
  }

  return 'Allow cookies'
}

function grantCategoryConsent(category) {
  const consent = getConsent()
  const nextConsent = consent ? consent.clone() : CookieConsent.WithNoConsent()

  if (category === CATEGORY_ANALYTICS) {
    nextConsent.analytics = true
  }

  if (category === CATEGORY_MARKETING) {
    nextConsent.marketing = true
  }

  saveConsentToCookie(nextConsent)
  dispatcher.dispatch(CONSENT_CHANGED_EVENT, { consent: nextConsent })
}

function replacePlaceholderWithIframe(placeholder) {
  if (!placeholder || !placeholder.parentNode) {
    return
  }

  const encodedIframe = placeholder.dataset.cookieConsentIframe
  const iframeMarkup = decodeIframeMarkup(encodedIframe)
  const iframe = parseIframe(iframeMarkup)
  if (!iframe) {
    return
  }

  placeholder.parentNode.replaceChild(iframe, placeholder)
}

function hydratePlaceholder(placeholder, consent) {
  const category = (placeholder.dataset.cookieConsentCategory || CATEGORY_MARKETING).trim().toLowerCase()

  if (hasConsentForCategory(consent, category)) {
    replacePlaceholderWithIframe(placeholder)
    return
  }

  applyPlaceholderHeight(placeholder)
  placeholder.innerHTML = ''

  const message = document.createElement('p')
  message.textContent = getMessageForCategory(category)

  const button = document.createElement('button')
  button.type = 'button'
  button.classList.add('button')
  button.textContent = getButtonLabelForCategory(category)
  button.addEventListener('click', () => {
    grantCategoryConsent(category)
    replacePlaceholderWithIframe(placeholder)
  })

  placeholder.appendChild(message)
  placeholder.appendChild(button)
}

function applyToPlaceholders(consent) {
  document.querySelectorAll('.cookie-consent-iframe-placeholder').forEach((placeholder) => {
    hydratePlaceholder(placeholder, consent)
  })
}

function setupCookieConsentIframes() {
  applyToPlaceholders(getConsent())

  if (listenerAdded) {
    return
  }

  dispatcher.addListener(CONSENT_CHANGED_EVENT, (ev) => {
    const consent = ev && ev.consent ? ev.consent : getConsent()
    applyToPlaceholders(consent)
  })

  listenerAdded = true
}

export { setupCookieConsentIframes }
